/**
 * fontLicense/LicenseConfirmationContext.tsx
 *
 * Local React state for the license confirmation modal (no Redux). The modal
 * registers its dispatch into LicenseConfirmationService via useEffect so the
 * Promise facade can open/close it. Mirrors MetadataEditorContext.tsx.
 */

import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { FontFileEntry } from './model';
import { _registerDispatch, _unregisterDispatch } from './LicenseConfirmationService';

export interface LicenseConfirmationState {
  open: boolean;
  fonts: FontFileEntry[];
  submitInProgress: boolean;
}

const initialState: LicenseConfirmationState = {
  open: false,
  fonts: [],
  submitInProgress: false,
};

type Action =
  | { type: 'OPEN'; fonts: FontFileEntry[] }
  | { type: 'CLOSE' }
  | { type: 'SET_SUBMIT_IN_PROGRESS'; value: boolean };

function reducer(state: LicenseConfirmationState, action: Action): LicenseConfirmationState {
  switch (action.type) {
    case 'OPEN':
      return { ...state, open: true, fonts: action.fonts, submitInProgress: false };
    case 'CLOSE':
      return initialState;
    case 'SET_SUBMIT_IN_PROGRESS':
      return { ...state, submitInProgress: action.value };
    default:
      return state;
  }
}

interface LicenseConfirmationContextValue {
  state: LicenseConfirmationState;
  dispatch: React.Dispatch<Action>;
}

const LicenseConfirmationCtx = createContext<LicenseConfirmationContextValue | null>(null);

export function useLicenseConfirmation() {
  const ctx = useContext(LicenseConfirmationCtx);
  if (!ctx) {
    throw new Error('useLicenseConfirmation must be used inside FontLicenseProvider');
  }
  return ctx;
}

export const FontLicenseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Register the reducer dispatch with the Promise facade while mounted.
  useEffect(() => {
    _registerDispatch(dispatch as (action: any) => void);
    return () => _unregisterDispatch();
  }, []);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <LicenseConfirmationCtx.Provider value={value}>{children}</LicenseConfirmationCtx.Provider>
  );
};
